/**
 * PERFORM — ACCESS TIERS
 * Single source of truth for all tier definitions.
 *
 * RULE: Never hardcode tier logic in components.
 * Always use these constants + useUserTier hook.
 *
 * TIER HIERARCHY: free < lifetime < subscriber
 *
 * PRICE PLACEHOLDERS: Replace PRICE_LIFETIME_EUR and
 * PRICE_SUBSCRIBER_EUR before going live.
 *
 * @since 3-Tier Access System v1
 */

export type PerformTier = "free" | "lifetime" | "subscriber";

export const TIER_RANK: Record<PerformTier, number> = {
  free: 0,
  lifetime: 1,
  subscriber: 2,
};

export const TIER_CONFIG = {
  free: {
    id: "free" as const,
    label: "Gratuit",
    description: "Accès aux modules validés et stables",
    price: null,
    color: "text-muted-foreground",
    badge: null,
    features: [
      "Tous les modules Advanced validés",
      "Génération de programmes par le Cerveau",
      "Planning hebdomadaire",
      "Suivi quotidien",
      "Bibliothèque scientifique (7 000+ faits)",
      "Communauté",
      "Mérites & Achievements",
    ],
  },
  lifetime: {
    id: "lifetime" as const,
    label: "Accès à vie",
    description: "Modules avancés en phase de test (95% finalisés)",
    // TODO: Replace with real price before going live
    price: "PRICE_LIFETIME_EUR",
    stripeMode: "payment" as const,
    color: "text-primary",
    badge: "✦ Vie",
    features: [
      "Tout le plan Gratuit",
      "Modules pro en phase de test (coach, santé, compléments...)",
      "Accès anticipé aux modules à 95%",
      "Support prioritaire",
      'Badge "Membre Fondateur" sur le profil',
    ],
  },
  subscriber: {
    id: "subscriber" as const,
    label: "Abonnement",
    description: "Dernières mises à jour, avant-premières, avis et propositions",
    // TODO: Replace with real price before going live
    price: "PRICE_SUBSCRIBER_EUR",
    stripeMode: "subscription" as const,
    color: "text-amber-500",
    badge: "⭐ Pro",
    features: [
      "Tout le plan Accès à vie",
      "Derniers modules en avant-première",
      "Possibilité de donner des avis et propositions",
      "Accès aux nouvelles features dès leur sortie",
      'Badge "Abonné Pro" sur le profil',
    ],
  },
} as const;

/**
 * Module access rules by tier.
 * Flags listed under a tier require AT LEAST that tier.
 * Flags not listed in any array are accessible to all (free).
 */
export const TIER_MODULE_ACCESS: Record<PerformTier, string[]> = {
  free: [
    "ACHIEVEMENTS",
    "BILLING_STRIPE",
    "NUTRITION_PLANNING",
    "PROGRAM_EXPORT_PDF",
    "CALENDAR_SYNC_ICAL",
    "MERIT_SHOP",
    "WELLNESS",
    "PROGRESSION_CHARTS",
  ],
  lifetime: [
    "NUTRITION_SUPPLEMENTS",
    "SOCIAL_FRIENDS",
    "ADMIN_REVIEW_HUB",
  ],
  subscriber: [
    "SCIENCE_CARDS",
    "CHALLENGES_UI",
  ],
};

/**
 * Check if a feature flag is accessible for a given tier.
 */
export function isFlagAccessibleForTier(
  flag: string,
  userTier: PerformTier
): boolean {
  const userRank = TIER_RANK[userTier];

  // Check subscriber-only flags
  if (TIER_MODULE_ACCESS.subscriber.includes(flag)) {
    return userRank >= TIER_RANK.subscriber;
  }

  // Check lifetime flags
  if (TIER_MODULE_ACCESS.lifetime.includes(flag)) {
    return userRank >= TIER_RANK.lifetime;
  }

  // Free tier or unlisted flags → accessible to all
  return true;
}

/**
 * Get the minimum tier required for a flag.
 */
export function getRequiredTierForFlag(flag: string): PerformTier {
  if (TIER_MODULE_ACCESS.subscriber.includes(flag)) return "subscriber";
  if (TIER_MODULE_ACCESS.lifetime.includes(flag)) return "lifetime";
  return "free";
}
